import { useEffect, useMemo, useState, useCallback } from "react";
import {
  BookOpen, Download, Search, Loader2, ChevronLeft, ChevronRight,
  ArrowUpRight, Sparkles, List, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const imgUrl = (slug) => (slug ? `${process.env.PUBLIC_URL || ""}/handbook/${slug}.jpg` : null);

function Shot({ slug, alt }) {
  const [ok, setOk] = useState(true);
  const url = imgUrl(slug);
  if (!url || !ok) return null;
  return (
    <figure className="my-4 rounded-xl overflow-hidden border border-[#1e3d3a]/12 bg-[#f4f2ea] shadow-sm">
      <img src={url} alt={alt} onError={() => setOk(false)} className="w-full block" data-testid={`handbook-shot-${slug}`} />
    </figure>
  );
}

function Steps({ items }) {
  if (!items?.length) return null;
  return (
    <ol className="mt-2 space-y-1.5 list-decimal pl-5 marker:text-[#c9a961] marker:font-semibold">
      {items.map((s, i) => <li key={i} className="text-[13.5px] text-[#1e3d3a]/80 leading-relaxed">{s}</li>)}
    </ol>
  );
}

export default function HandbookBoard({ onOpenTab, handbookPath = "/admin/handbook", pdfPath = "/admin/handbook/pdf", downloadName = "Elevare-Training-Handbook.pdf" }) {
  const [hb, setHb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [pos, setPos] = useState(0);
  const [anim, setAnim] = useState("in");

  useEffect(() => {
    api.get(handbookPath).then(({ data }) => setHb(data))
      .catch(() => toast.error("Couldn't load the handbook.")).finally(() => setLoading(false));
  }, [handbookPath]);

  // Flatten the book into a linear list of "views".
  const views = useMemo(() => {
    if (!hb) return [];
    const v = [{ kind: "cover" }, { kind: "contents" }, { kind: "whatsnew" }];
    hb.parts.forEach((p) => p.chapters.forEach((c) => v.push({ kind: "chapter", part: p, chapter: c })));
    return v;
  }, [hb]);

  const chapterViewIndex = useCallback((number) =>
    views.findIndex((v) => v.kind === "chapter" && v.chapter.number === number), [views]);

  const go = useCallback((next) => {
    setPos((cur) => {
      const t = Math.max(0, Math.min(views.length - 1, next));
      if (t !== cur) { setAnim("out"); setTimeout(() => setAnim("in"), 20); }
      return t;
    });
  }, [views.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (q) return;
      if (e.key === "ArrowRight") go(pos + 1);
      if (e.key === "ArrowLeft") go(pos - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pos, go, q]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(pdfPath, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = downloadName; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Couldn't generate the PDF."); }
    finally { setDownloading(false); }
  };

  const searchHits = useMemo(() => {
    if (!hb || !q) return [];
    const needle = q.toLowerCase();
    const hits = [];
    hb.parts.forEach((p) => p.chapters.forEach((c) => {
      const hay = `${c.title} ${c.summary} ${c.body} ${(c.how_to || []).join(" ")} ${(c.subsections || []).map((x) => `${x.title} ${x.body} ${(x.how_to || []).join(" ")}`).join(" ")}`.toLowerCase();
      if (hay.includes(needle)) hits.push({ part: p, chapter: c });
    }));
    return hits;
  }, [hb, q]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#c9a961]" /></div>;
  if (!hb) return null;

  const view = views[pos] || views[0];
  const jumpTo = (number) => go(chapterViewIndex(number));

  const pageLabel = view.kind === "chapter"
    ? `Page ${view.chapter.number} of ${hb.total_pages}`
    : view.kind === "cover" ? "Cover" : view.kind === "contents" ? "Contents" : "What’s New";

  return (
    <div data-testid="handbook-board">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 text-[#1e3d3a]">
          <BookOpen className="h-5 w-5 text-[#c9a961]" />
          <h2 className="font-head text-2xl">Training Handbook</h2>
          <span className="text-[11px] text-[#1e3d3a]/45 ml-1">v{hb.version}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1e3d3a]/35" />
            <input data-testid="handbook-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the book…"
              className="w-56 rounded-full border border-[#1e3d3a]/15 bg-white pl-9 pr-8 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-4 w-4" /></button>}
          </div>
          <button onClick={download} disabled={downloading} data-testid="handbook-download"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
          </button>
        </div>
      </div>

      {/* Search results */}
      {q ? (
        <div data-testid="handbook-search-results" className="rounded-2xl border border-[#1e3d3a]/10 bg-white p-5">
          <p className="text-[13px] text-[#1e3d3a]/55 mb-3">{searchHits.length} chapter{searchHits.length === 1 ? "" : "s"} match “{q}”.</p>
          <div className="space-y-2">
            {searchHits.map(({ part, chapter }) => (
              <button key={chapter.number} data-testid={`handbook-hit-${chapter.number}`}
                onClick={() => { setQ(""); jumpTo(chapter.number); }}
                className="w-full text-left rounded-xl border border-[#1e3d3a]/10 px-4 py-3 hover:bg-[#1e3d3a]/[0.03] transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-[#c9a961]">Ch. {chapter.number}</span>
                  <span className="text-[11px] text-[#1e3d3a]/40">· {part.title}</span>
                </div>
                <div className="text-sm font-semibold text-[#1e3d3a]">{chapter.title}</div>
                <div className="text-[12.5px] text-[#1e3d3a]/60">{chapter.summary}</div>
              </button>
            ))}
            {searchHits.length === 0 && <p className="text-sm text-[#1e3d3a]/50 py-6 text-center">Nothing found. Try another word.</p>}
          </div>
        </div>
      ) : (
        <>
          {/* The book */}
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white shadow-[0_1px_0_rgba(30,61,58,0.05)] overflow-hidden">
            <div key={pos} className={`transition-all duration-300 ${anim === "out" ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0"}`}>
              <div className="p-6 sm:p-10 min-h-[560px]" data-testid={`handbook-view-${view.kind}`}>

                {view.kind === "cover" && (
                  <div className="flex flex-col items-center justify-center text-center py-16">
                    <div className="text-[11px] tracking-[0.35em] uppercase text-[#c9a961] font-semibold">Elevare Solutions</div>
                    <h1 className="font-head text-5xl lg:text-6xl text-[#1e3d3a] mt-4">Training<br />Handbook</h1>
                    <div className="w-24 h-[3px] bg-[#c9a961] my-6" />
                    <p className="max-w-md text-[15px] text-[#1e3d3a]/65 leading-relaxed">Your complete, illustrated operator’s guide — every portal, every tab, with a picture of the exact screen and step-by-step how-to.</p>
                    <p className="text-[12px] text-[#1e3d3a]/40 mt-6">Version {hb.version} · updated {hb.generated_at?.slice(0, 10)} · {hb.total_pages} chapters</p>
                    <button onClick={() => go(1)} data-testid="handbook-open-book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] px-6 py-3 text-sm font-semibold text-white hover:bg-[#2a534d]">
                      Open the book <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {view.kind === "contents" && (
                  <div data-testid="handbook-contents">
                    <div className="flex items-center gap-2 mb-1"><List className="h-5 w-5 text-[#c9a961]" /><h3 className="font-head text-3xl text-[#1e3d3a]">Contents</h3></div>
                    <p className="text-[13px] text-[#1e3d3a]/55 mb-6">Tap any chapter to jump straight to it.</p>
                    {hb.parts.map((p) => (
                      <div key={p.id} className="mb-6">
                        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#c9a961]">{p.part}</div>
                        <div className="text-lg font-head text-[#1e3d3a] mb-2">{p.title}</div>
                        <div className="divide-y divide-[#1e3d3a]/8 border-t border-[#1e3d3a]/8">
                          {p.chapters.map((c) => (
                            <button key={c.number} data-testid={`handbook-goto-${c.number}`} onClick={() => jumpTo(c.number)}
                              className="w-full flex items-baseline gap-3 py-2.5 text-left group">
                              <span className="text-[12px] font-semibold text-[#1e3d3a]/40 w-6 shrink-0">{c.number}</span>
                              <span className="flex-1 min-w-0">
                                <span className="text-sm font-semibold text-[#1e3d3a] group-hover:text-[#a3823f] transition-colors">{c.title}</span>
                                <span className="block text-[12px] text-[#1e3d3a]/55 truncate">{c.summary}</span>
                              </span>
                              <span className="text-[12px] text-[#1e3d3a]/40 shrink-0">p. {c.number}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {view.kind === "whatsnew" && (
                  <div data-testid="handbook-whatsnew">
                    <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-[#c9a961]" /><h3 className="font-head text-3xl text-[#1e3d3a]">What’s New</h3></div>
                    <p className="text-[13px] text-[#1e3d3a]/55 mb-5">Recently shipped improvements across the platform.</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {hb.changelog?.map((c, i) => (
                        <div key={i} className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-4 py-3.5" data-testid={`handbook-changelog-item-${i}`}>
                          <div className="text-[10.5px] font-semibold text-[#c9a961] tracking-wide">{c.date}</div>
                          <div className="text-sm font-semibold text-[#1e3d3a] mt-0.5">{c.title}</div>
                          <p className="text-[12.5px] text-[#1e3d3a]/65 leading-relaxed mt-1">{c.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {view.kind === "chapter" && (
                  <article data-testid={`handbook-page-${view.chapter.number}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#c9a961]">{view.part.part} · Chapter {view.chapter.number}</span>
                    </div>
                    <h3 className="font-head text-3xl text-[#1e3d3a] mt-1">{view.chapter.title}</h3>
                    {view.chapter.summary && <p className="text-[14px] italic text-[#1e3d3a]/60 mt-1.5">{view.chapter.summary}</p>}
                    <Shot slug={view.chapter.image} alt={view.chapter.title} />
                    {view.chapter.body && <p className="text-[14px] text-[#1e3d3a]/85 leading-relaxed">{view.chapter.body}</p>}
                    {view.chapter.how_to?.length > 0 && (
                      <>
                        <div className="text-[12px] font-semibold tracking-[0.15em] uppercase text-[#1e3d3a]/50 mt-5">How to use it</div>
                        <Steps items={view.chapter.how_to} />
                      </>
                    )}
                    {view.chapter.subsections?.map((sub, i) => (
                      <div key={i} className="mt-4 border-l-2 border-[#c9a961]/40 pl-4">
                        <div className="text-sm font-semibold text-[#1e3d3a]">{sub.title}</div>
                        {sub.body && <p className="text-[13px] text-[#1e3d3a]/70 leading-relaxed mt-0.5">{sub.body}</p>}
                        <Steps items={sub.how_to} />
                      </div>
                    ))}
                    {view.chapter.images?.length > 0 && (
                      <div className="mt-5 space-y-4" data-testid="handbook-figures">
                        {view.chapter.images.map((fig, i) => (
                          <figure key={i} className="rounded-xl overflow-hidden border border-[#1e3d3a]/12 bg-[#f4f2ea]" data-testid={`handbook-figure-${fig.slug}`}>
                            <img src={imgUrl(fig.slug)} alt={fig.caption || view.chapter.title} className="w-full block"
                              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                            {fig.caption && <figcaption className="px-3 py-2 text-[12px] italic text-[#1e3d3a]/60 text-center border-t border-[#1e3d3a]/8">{fig.caption}</figcaption>}
                          </figure>
                        ))}
                      </div>
                    )}
                    {view.part.id === "admin" && view.chapter.tab && onOpenTab && (
                      <button data-testid={`handbook-open-${view.chapter.tab}`} onClick={() => onOpenTab(view.chapter.tab)}
                        className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#a3823f] transition-colors">
                        <ArrowUpRight className="h-4 w-4" /> Open this tool
                      </button>
                    )}
                  </article>
                )}
              </div>
            </div>

            {/* Pager */}
            <div className="flex items-center justify-between border-t border-[#1e3d3a]/10 bg-[#eae7de]/40 px-5 py-3">
              <button data-testid="handbook-prev" onClick={() => go(pos - 1)} disabled={pos === 0}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-[#1e3d3a] hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent">
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <div className="flex items-center gap-3">
                <button onClick={() => go(1)} className="text-[12px] font-semibold text-[#1e3d3a]/55 hover:text-[#a3823f]">Index</button>
                <span className="text-[12px] text-[#1e3d3a]/50" data-testid="handbook-page-label">{pageLabel}</span>
              </div>
              <button data-testid="handbook-next" onClick={() => go(pos + 1)} disabled={pos === views.length - 1}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-[#1e3d3a] hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent">
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
